import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import type { ChatMessage as ChatMessageType } from '../../store/chatBotSlice';
import { updateMessageStatus, createFreshdeskTicket, addMessage } from '../../store/chatBotSlice';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';
import { SearchingLoader } from './SearchingLoader';

interface ChatMessageListProps {
  messages: ChatMessageType[];
  isTyping: boolean;
  sessionId: string | null;
  isLoading: boolean;
  className?: string;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isTyping,
  sessionId,
  isLoading,
  className = ''
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = (smooth: boolean = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest'
      });
    }
  };

  // Scroll to bottom on new messages or typing indicator
  useEffect(() => {
    // Always scroll to bottom when new messages arrive or typing starts
    scrollToBottom();
  }, [messages.length, isTyping]);

  // Scroll to bottom when component first mounts (when chat opens)
  useEffect(() => {
    // Use a small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      scrollToBottom(false); // Instant scroll, no smooth animation
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array means this runs only once on mount

  // Handle message retry
  const handleRetryMessage = async (messageId: string) => {
    const failedMessage = messages.find(msg => msg.id === messageId);
    if (!failedMessage || !sessionId) return;

    // Update message status to sending (this updates the existing message in UI)
    dispatch(updateMessageStatus({ messageId, status: 'sending' }));

    try {
      // Call API directly without using sendMessage to avoid creating duplicate
      const { chatBotAPI } = await import('../../services/chatBotAPI');
      const response = await chatBotAPI.sendMessage(failedMessage.content, sessionId);

      if (response.success && response.agentResponse) {
        // Update the original message to sent
        dispatch(updateMessageStatus({ messageId, status: 'sent' }));

        // Add only the agent response
        dispatch(addMessage(response.agentResponse));
      } else {
        throw new Error(response.error?.message || 'Failed to send message');
      }
    } catch (error) {
      // Revert to failed status
      dispatch(updateMessageStatus({ messageId, status: 'failed' }));
    }
  };

  // Handle Freshdesk ticket creation
  const handleCreateTicket = async (ticketSessionId: string) => {
    if (!ticketSessionId) {
      console.error('No session ID available for ticket creation');
      return;
    }

    try {
      await dispatch(createFreshdeskTicket(ticketSessionId)).unwrap();
      // Success message is added by the Redux action
    } catch (error) {
      console.error('Error creating Freshdesk ticket:', error);
      // Error message is added by the Redux action
    }
  };

  // Memoized message components for performance
  const messageComponents = useMemo(() => {
    return messages.map((message, index) => {
      // Detect if this is the greeting message (first assistant message)
      const isGreeting = message.role === 'assistant' && index === 0;

      return (
        <ChatMessage
          key={message.id}
          message={message}
          isLast={index === messages.length - 1}
          isGreeting={isGreeting}
          onRetry={handleRetryMessage}
          onCreateTicket={handleCreateTicket}
        />
      );
    });
  }, [messages]);

  // Empty state - removed to wait for API greeting message
  // if (messages.length === 0 && !isTyping) {
  //   return empty state UI
  // }

  return (
    <div
      ref={messagesContainerRef}
      className={`chat-message-list flex-1 overflow-y-auto ${className}`}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-atomic="false"
      aria-label="Chat conversation"
      style={{
        scrollBehavior: 'smooth',
        scrollbarWidth: 'thin',
        scrollbarColor: '#CBD5E0 transparent'
      }}
    >
      {/* Message List */}
      <div className="py-4 space-y-1">
        {messageComponents}
        
        {/* Typing Indicator */}
        {isTyping && (
          <TypingIndicator isVisible={true} />
        )}

        {/* Modern Searching Loader (when processing) */}
        {isLoading && !isTyping && (
          <SearchingLoader message="Analyzing your query..." />
        )}
        
        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

// Enhanced version with virtual scrolling (for large message lists)
interface VirtualizedChatMessageListProps extends ChatMessageListProps {
  itemHeight?: number;
  overscan?: number;
}

export const VirtualizedChatMessageList: React.FC<VirtualizedChatMessageListProps> = ({
  messages,
  isTyping,
  sessionId,
  isLoading,
  className = '',
  itemHeight = 80,
  overscan = 5
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [startIndex, setStartIndex] = React.useState(0);
  const [endIndex, setEndIndex] = React.useState(20);

  // Simple virtualization logic
  useEffect(() => {
    const container = containerRef.current;
    if (!container || messages.length <= 20) return; // Only virtualize for large lists

    const handleScroll = () => {
      const { scrollTop, clientHeight } = container;
      const newStartIndex = Math.floor(scrollTop / itemHeight);
      const visibleCount = Math.ceil(clientHeight / itemHeight);
      const newEndIndex = Math.min(
        messages.length,
        newStartIndex + visibleCount + overscan
      );
      
      setStartIndex(Math.max(0, newStartIndex - overscan));
      setEndIndex(newEndIndex);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages.length, itemHeight, overscan]);

  // For small lists, use regular rendering
  if (messages.length <= 50) {
    return (
      <ChatMessageList
        messages={messages}
        isTyping={isTyping}
        sessionId={sessionId}
        isLoading={isLoading}
        className={className}
      />
    );
  }

  // Virtual scrolling for large lists
  const dispatch = useDispatch<AppDispatch>();
  const visibleMessages = messages.slice(startIndex, endIndex);
  const totalHeight = messages.length * itemHeight;
  const offsetY = startIndex * itemHeight;

  // Handle Freshdesk ticket creation
  const handleCreateTicket = async (ticketSessionId: string) => {
    if (!ticketSessionId) {
      console.error('No session ID available for ticket creation');
      return;
    }

    try {
      await dispatch(createFreshdeskTicket(ticketSessionId)).unwrap();
    } catch (error) {
      console.error('Error creating Freshdesk ticket:', error);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`chat-message-list flex-1 overflow-y-auto ${className}`}
      style={{
        scrollBehavior: 'smooth',
        scrollbarWidth: 'thin',
        scrollbarColor: '#CBD5E0 transparent'
      }}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleMessages.map((message, index) => {
            const actualIndex = startIndex + index;
            const isGreeting = message.role === 'assistant' && actualIndex === 0;

            return (
              <div key={message.id} style={{ height: itemHeight }}>
                <ChatMessage
                  message={message}
                  isLast={actualIndex === messages.length - 1}
                  isGreeting={isGreeting}
                  onCreateTicket={handleCreateTicket}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Typing indicator always at bottom */}
      {isTyping && (
        <div style={{ position: 'sticky', bottom: 0 }}>
          <TypingIndicator isVisible={true} />
        </div>
      )}
    </div>
  );
};

export default ChatMessageList;